import React from 'react';
import { TextField } from '@material-ui/core';
import { VariableEditorProps } from './types';
import {
  StringEditor,
  NumberEditor,
  BoolEditor,
  ListEditor,
  MapEditor,
  ObjectEditor,
} from './editors';
import { NullableWrapper } from './NullableWrapper';

/**
 * VariableEditor - Routes to the appropriate editor component based on variable type
 *
 * For nullable variables, wraps the editor with NullableWrapper to provide:
 * - A "null" button on the right side to set the value to null
 * - When null, shows "null" text and an "Edit" button to restore editing
 */
export const VariableEditor: React.FC<VariableEditorProps> = props => {
  const { variable, value } = props;

  // For nullable variables with null value, NullableWrapper handles the display
  // We need to determine what editor to show when "Edit" is clicked
  const getEditorForType = () => {
    switch (variable.type) {
      case 'string':
        return <StringEditor {...props} />;

      case 'number':
        return <NumberEditor {...props} />;

      case 'bool':
        return <BoolEditor {...props} />;

      case 'list':
      case 'set': {
        // If value is a string that looks like JSON array, parse it first
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
            try {
              const parsed = JSON.parse(trimmed);
              if (Array.isArray(parsed)) {
                return <ListEditor {...props} value={parsed} onChange={(newVal) => props.onChange(newVal)} />;
              }
            } catch {
              // Not valid JSON, fall through to regular handling
            }
          }
        }
        return <ListEditor {...props} />;
      }

      case 'map': {
        // If value is a string that looks like JSON object, parse it first
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
            try {
              const parsed = JSON.parse(trimmed);
              if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return <MapEditor {...props} value={parsed} onChange={(newVal) => props.onChange(newVal)} />;
              }
            } catch {
              // Not valid JSON, fall through to regular handling
            }
          }
        }
        return <MapEditor {...props} />;
      }

      case 'object': {
        // If value is a string that looks like JSON, parse it first
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
            try {
              const parsed = JSON.parse(trimmed);
              if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return <ObjectEditor {...props} value={parsed} onChange={(newVal) => props.onChange(newVal)} />;
              }
            } catch {
              // Not valid JSON, fall through to regular handling
            }
          }
        }
        return <ObjectEditor {...props} />;
      }

      case 'tuple':
        return <ListEditor {...props} />;

      case 'any':
      case 'dynamic':
      default: {
        // For any/dynamic types, infer from value type and route to appropriate editor
        // If value is null, we can't infer type - show a JSON text editor
        if (value === null) {
          return (
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={variable.name}
              multiline
              minRows={2}
              value=""
              onChange={e => {
                const val = e.target.value;
                try {
                  props.onChange(JSON.parse(val));
                } catch {
                  props.onChange(val);
                }
              }}
              error={!!props.error}
              helperText={props.error || variable.description}
              disabled={props.disabled}
              placeholder="Enter value (JSON or string)"
            />
          );
        }

        // If value is an object (but not null and not array), use ObjectEditor
        if (typeof value === 'object' && !Array.isArray(value)) {
          return <ObjectEditor {...props} />;
        }

        // If value is an array, use ListEditor
        if (Array.isArray(value)) {
          return <ListEditor {...props} />;
        }

        // If value looks like a boolean, use BoolEditor
        if (typeof value === 'boolean') {
          return <BoolEditor {...props} />;
        }

        // If value is a number, use NumberEditor
        if (typeof value === 'number') {
          return <NumberEditor {...props} />;
        }

        // If value is a string that looks like JSON object/array, try to parse and route appropriately
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
              (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
            try {
              const parsed = JSON.parse(trimmed);
              // If it parses to an object, use ObjectEditor with parsed value
              if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return <ObjectEditor {...props} value={parsed} onChange={(newVal) => props.onChange(newVal)} />;
              }
              // If it parses to an array, use ListEditor with parsed value
              if (Array.isArray(parsed)) {
                return <ListEditor {...props} value={parsed} onChange={(newVal) => props.onChange(newVal)} />;
              }
            } catch {
              // Not valid JSON, fall through to string editor
            }
          }
        }

        // For strings, use a JSON-aware text editor
        return (
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label={variable.name}
            multiline
            minRows={2}
            value={
              typeof value === 'string'
                ? value
                : JSON.stringify(value, null, 2)
            }
            onChange={e => {
              const val = e.target.value;
              try {
                props.onChange(JSON.parse(val));
              } catch {
                props.onChange(val);
              }
            }}
            error={!!props.error}
            helperText={props.error || variable.description}
            disabled={props.disabled}
            placeholder={
              variable.currentValue !== undefined
                ? `Current: ${JSON.stringify(variable.currentValue)}`
                : variable.default !== undefined
                  ? `Default: ${JSON.stringify(variable.default)}`
                  : undefined
            }
          />
        );
      }
    }
  };

  // Wrap with NullableWrapper for nullable variables
  return (
    <NullableWrapper
      variable={variable}
      value={value}
      onChange={props.onChange}
      disabled={props.disabled}
    >
      {getEditorForType()}
    </NullableWrapper>
  );
};
